"use client"

import { useRef, useState, type ChangeEvent } from "react"
import { Button } from "@/components/ui/button"
import { Upload, Trash2, Loader2 } from "lucide-react"
import { useSharedUiTranslations } from "@/components/i18n/ui-control-labels-context"


const DEFAULT_ACCEPT = "image/svg+xml,image/png,image/jpeg,image/webp"
const DEFAULT_MAX_SIZE_MB = 2

type UploadError = { kind: "type"; accepted: string } | { kind: "size"; maxSizeMB: number }

interface ImageUploadProps {
  currentImageUrl: string | null
  onUpload: (file: File) => Promise<void>
  onRemove: () => Promise<void>
  isUploading?: boolean
  accept?: string
  maxSizeMB?: number
  shape?: "circle" | "auto"
  previewClassName?: string
}

export function ImageUpload({
  currentImageUrl,
  onUpload,
  onRemove,
  isUploading = false,
  accept = DEFAULT_ACCEPT,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  shape = "auto",
  previewClassName,
}: ImageUploadProps) {
  const tUi = useSharedUiTranslations("ImageUpload")

  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<UploadError | null>(null)

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset for next selection
    e.target.value = ""

    // Validate type
    const acceptedTypes = accept.split(",").map((t) => t.trim())
    if (!acceptedTypes.includes(file.type)) {
      setError({ kind: "type", accepted: acceptedTypes.map(type => type.split("/")[1]).join(", ") })
      return
    }

    // Validate size
    const maxBytes = maxSizeMB * 1024 * 1024
    if (file.size > maxBytes) {
      setError({ kind: "size", maxSizeMB })
      return
    }

    setError(null)
    await onUpload(file)
  }

  const errorText = error ? uploadErrorText(error, tUi) : null
  const shapeClass = shape === "circle" ? "rounded-full" : "rounded-lg"
  const defaultPreviewClass = `h-16 w-auto max-w-[200px] object-contain ${shapeClass}`

  return (
    <div className="space-y-3">
      {currentImageUrl && (
        <div className="flex items-center gap-4">
          {/* eslint-disable-next-line @next/next/no-img-element -- User logos include local data URLs and retain their intrinsic aspect ratio. */}
          <img
            src={currentImageUrl}
            alt={tUi("currentLogo")}
            className={previewClassName ?? defaultPreviewClass}
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFileChange}
          disabled={isUploading}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          <span key={isUploading ? tUi("uploading") : tUi("upload")}>{isUploading ? tUi("uploading") : tUi("upload")}</span>
        </Button>

        {currentImageUrl && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isUploading}
            onClick={onRemove}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span><span key={tUi("remove")}>{tUi("remove")}</span></span>
          </Button>
        )}
      </div>

      {errorText && <p key={errorText} className="text-sm text-red-600">{errorText}</p>}

      <p className="text-xs text-muted-foreground">
        <span key={tUi("acceptedFileHint", {maxSizeMB})}>{tUi("acceptedFileHint", {maxSizeMB})}</span>
      </p>
    </div>
  )
}

function uploadErrorText(error: UploadError, tUi: ReturnType<typeof useSharedUiTranslations<"ImageUpload">>): string {
  switch (error.kind) {
    case "type": return tUi("unsupportedFileType", { accepted: error.accepted })
    case "size": return tUi("fileTooLarge", { maxSizeMB: error.maxSizeMB })
  }
}
